import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

const LanguageContext = createContext(null);
export const useLang = () => useContext(LanguageContext);

const DICT = {
  es: {
    nav_home: "Inicio", nav_catalog: "Catálogo", nav_athletes: "Atletas",
    nav_about: "Sobre VORNA", nav_contact: "Contacto",
    cta_catalog: "Ver catálogo", cta_about: "Conoce VORNA", cta_athletes: "Nuestros atletas",
    cta_explore: "Explorar productos", view_more: "Ver más",
    more_info: "Más información", check_availability: "Consultar disponibilidad",
    contact_vorna: "Contactar a VORNA", price_on_request: "Precio bajo consulta",
    all: "Todos", filters: "Filtros", clear: "Limpiar", category: "Categoría",
    discipline: "Disciplina", size: "Talla", color: "Color", gender: "Género",
    products: "productos", no_products: "No se encontraron productos",
    men: "Hombre", women: "Mujer", unisex: "Unisex", everyone: "Todos",
    sizes_available: "Tallas disponibles", colors_available: "Colores disponibles",
    materials: "Materiales", features: "Características", intended_use: "Uso recomendado",
    care: "Cuidados", worn_by: "Usado por atletas", you_may_like: "También te puede interesar",
    size_guide: "Guía de tallas", related: "Relacionados",
    athletes_title: "Nuestros Atletas", about_title: "Sobre VORNA", contact_title: "Contáctanos",
    record: "Récord", weight_class: "Categoría", team: "Equipo / Academia",
    city: "Ciudad", disciplineL: "Disciplina", achievements: "Logros",
    bio: "Biografía", gear_they_use: "Equipo que usan", follow: "Seguir",
    back_athletes: "Todos los atletas", all_athletes: "Conoce a nuestros atletas",
    whatsapp_product: "Hola VORNA, me interesa el producto:",
    footer_tagline: "Nacido en Ecuador. Hecho para la pelea.",
    lang_label: "Idioma",
  },
  en: {
    nav_home: "Home", nav_catalog: "Catalogue", nav_athletes: "Athletes",
    nav_about: "About VORNA", nav_contact: "Contact",
    cta_catalog: "View catalogue", cta_about: "Discover VORNA", cta_athletes: "Our athletes",
    cta_explore: "Explore products", view_more: "View more",
    more_info: "More info", check_availability: "Check availability",
    contact_vorna: "Contact VORNA", price_on_request: "Price on request",
    all: "All", filters: "Filters", clear: "Clear", category: "Category",
    discipline: "Discipline", size: "Size", color: "Color", gender: "Gender",
    products: "products", no_products: "No products found",
    men: "Men", women: "Women", unisex: "Unisex", everyone: "Everyone",
    sizes_available: "Available sizes", colors_available: "Available colors",
    materials: "Materials", features: "Features", intended_use: "Intended use",
    care: "Care", worn_by: "Worn by athletes", you_may_like: "You may also like",
    size_guide: "Size guide", related: "Related",
    athletes_title: "Our Athletes", about_title: "About VORNA", contact_title: "Contact Us",
    record: "Record", weight_class: "Weight Class", team: "Team / Academy",
    city: "City", disciplineL: "Discipline", achievements: "Achievements",
    bio: "Biography", gear_they_use: "Gear they use", follow: "Follow",
    back_athletes: "All athletes", all_athletes: "Meet our athletes",
    whatsapp_product: "Hi VORNA, I'm interested in the product:",
    footer_tagline: "Born in Ecuador. Built for the fight.",
    lang_label: "Language",
  },
};

const CAT_LABELS = {
  "mma-shorts": { es: "Pantalonetas MMA", en: "MMA Shorts" },
  "muay-thai-shorts": { es: "Pantalonetas Muay Thai", en: "Muay Thai Shorts" },
  "shorts": { es: "Pantalonetas", en: "Shorts" },
  "tops": { es: "Tops y Rashes", en: "Tops & Rash Guards" },
  "sets": { es: "Conjuntos", en: "Sets" },
};

const DISC_LABELS = {
  mma: { es: "MMA", en: "MMA" },
  training: { es: "Entrenamiento", en: "Training" },
  bjj: { es: "BJJ / Jiu-Jitsu", en: "BJJ / Jiu-Jitsu" },
  grappling: { es: "Grappling", en: "Grappling" },
};

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem("vorna_lang") || "es");
  useEffect(() => {
    localStorage.setItem("vorna_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);
  const setLang = useCallback((l) => setLangState(l), []);

  const t = useCallback((key) => DICT[lang][key] ?? DICT.es[key] ?? key, [lang]);
  const tr = useCallback((es, en) => (lang === "es" ? es : en), [lang]);
  const L = useCallback((obj, field) => {
    if (!obj) return "";
    if (lang === "en") return obj[`${field}_en`] || obj[field] || "";
    return obj[field] || "";
  }, [lang]);
  const catLabel = useCallback((key) => (CAT_LABELS[key]?.[lang] ?? key), [lang]);
  const discLabel = useCallback((key) => (DISC_LABELS[key]?.[lang] ?? key), [lang]);
  const priceText = useCallback(
    (price) => (price ? `$${Number(price).toFixed(2)}` : DICT[lang].price_on_request),
    [lang]
  );

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, tr, L, catLabel, discLabel, priceText }}>
      {children}
    </LanguageContext.Provider>
  );
}
